/**
 * SPRINT V4.4 — Cinematic Moments Reel.
 * Vertical, snap-scroll reel (reels-style). Each moment: tipo, emoji,
 * titular, micro-descripción, CTA share canónico.
 */
import { useEffect, useRef } from "react";
import { Share2 } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";

export type CinematicMomentKind = "goal" | "comeback" | "card" | "record" | "national_milestone";

export interface CinematicMoment {
  id: string;
  kind: CinematicMomentKind;
  title: string;
  subtitle: string;
  emoji: string;
  nationCode?: string | null;
  matchId?: string | null;
}

import { PREVIEW_MODE } from "@/lib/previewMode";

const DEFAULT_MOMENTS: CinematicMoment[] = [
  { id: "m1", kind: "goal", title: "⚽ Gol de último minuto", subtitle: PREVIEW_MODE ? "España igualó 2-2 a Argentina" : "México igualó 2-2 a Argentina", emoji: "⚽", nationCode: PREVIEW_MODE ? "ESP" : "MEX" },
  { id: "m2", kind: "comeback", title: "🔥 Remontada épica", subtitle: "Brasil vino desde 0-3 abajo", emoji: "🔥", nationCode: "BRA" },
  { id: "m3", kind: "card", title: "🟥 Roja directa", subtitle: "Capitán expulsado en el 78'", emoji: "🟥" },
  { id: "m4", kind: "record", title: "📈 Récord roto", subtitle: "Máximo goleador del torneo", emoji: "📈" },
  { id: "m5", kind: "national_milestone", title: "🏆 Hito nacional", subtitle: "Tu nación entró al top 3 mundial", emoji: "🏆", nationCode: PREVIEW_MODE ? "ESP" : "MEX" },
];

export function CinematicMomentsReel({ moments = DEFAULT_MOMENTS }: { moments?: CinematicMoment[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (en.isIntersecting && en.intersectionRatio > 0.6) {
            const id = (en.target as HTMLElement).dataset.id;
            if (id && !seenRef.current.has(id)) {
              seenRef.current.add(id);
              const m = moments.find((x) => x.id === id);
              void trackEvent("cinematic_moment_viewed", { id, kind: m?.kind ?? null });
            }
          }
        }
      },
      { root: el, threshold: [0.6] },
    );
    el.querySelectorAll<HTMLElement>("[data-cinematic-moment]").forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [moments]);

  const onShare = (m: CinematicMoment) => {
    const url = buildCanonicalShareUrl(`/?moment=${encodeURIComponent(m.id)}`, {
      source: "cinematic_reel", medium: "native_share", campaign: "v4_addiction",
    });
    const text = `${m.emoji} ${m.title} — ${m.subtitle}`;
    void trackEvent("cinematic_moment_shared", { id: m.id, kind: m.kind });
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator.share({ title: "VerseSport", text, url }).catch(() => { window.open(url, "_blank", "noopener,noreferrer"); });
    } else if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      ref={containerRef}
      className="snap-y snap-mandatory overflow-y-auto rounded-2xl border border-border/60 bg-background/40"
      style={{ maxHeight: "60vh" }}
    >
      {moments.map((m) => (
        <article
          key={m.id}
          data-cinematic-moment
          data-id={m.id}
          className="relative flex h-[55vh] snap-start flex-col items-center justify-center gap-3 px-4 text-center"
        >
          <div className="text-7xl drop-shadow-lg">{m.emoji}</div>
          <h3 className="text-lg font-black">{m.title}</h3>
          <p className="text-xs text-muted-foreground">{m.subtitle}</p>
          <button
            onClick={() => onShare(m)}
            className="mt-2 flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground"
          >
            <Share2 className="h-3 w-3" /> Compartir
          </button>
        </article>
      ))}
    </div>
  );
}